import React, { useEffect, useState, useRef } from "react";
import AdminSearchBar from "../../components/AdminSideComponents/AdminSearchBar";
import axiosInstance from "../../utils/axios";
import { Link, useNavigate } from "react-router-dom";
import { FaRegEdit, FaEllipsisV, FaCrown } from "react-icons/fa";
import { MdDeleteOutline } from "react-icons/md";
import { toast } from "react-hot-toast";
import Pagination from "../../components/AdminSideComponents/AdminPagination";
import DeleteConfirmationModal from "../../components/AdminSideComponents/DeleteConfirmationModal";
import SpecialCampaignModal from "../../components/AdminSideComponents/Modals/SpecialCampaignModal";

const NoCampaignFound = ({ query }) => {
  return (
    <div className="w-full text-center py-10 flex flex-col items-center justify-center">
      <p className="text-lg font-semibold text-gray-700">
        No campaigns found for "{query}"
      </p>
      <p className="text-sm text-gray-500 mt-2">
        Try searching something else.
      </p>
    </div>
  );
};

// const Range = ({ percentage }) => {
//   return (
//     <div className="w-full border-2 border-gray-400 rounded-xl relative my-2 px-4">
//       {/* FILL */}
//       <div
//         style={{ right: `calc(${100 - percentage}%)` }}
//         className=" border-2 border-[#B250D2] rounded-xl absolute -translate-y-[50%] -left-0.5"
//       ></div>

//       {/* BUBBLE */}
//       <div
//         style={{ left: `calc(${percentage}% - 16px)` }}
//         className="bubble absolute -translate-y-[50%]  bg-[#B250D2] py-1 px-1.5 text-white text-[0.5rem] rounded-lg"
//       >
//         {percentage}%
//       </div>
//     </div>
//   );
// };

// const getPercentage = (raised, goal) => {
//   if (!goal || goal === 0) return 0;
//   return Math.round((Number(raised) / Number(goal)) * 100);
// };

const CampaignSkeletonLoader = () => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
      {Array.from({ length: 6 }).map((_, idx) => (
        <div
          key={idx}
          className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse"
        >
          {/* Image Placeholder */}
          <div className="h-44 w-full bg-gray-200" />

          {/* Content Placeholder */}
          <div className="p-4 space-y-3">
            <div className="h-4 bg-gray-300 rounded w-3/4" />
            <div className="h-3 bg-gray-200 rounded w-full" />
            <div className="flex justify-between pt-2">
              <div className="h-3 bg-gray-300 rounded w-1/3" />
              <div className="h-3 bg-gray-300 rounded w-1/3" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Dropdown Menu Component
const CampaignDropdownMenu = ({
  campaign,
  onEdit,
  onDelete,
  onMakeSpecial,
  isOpen,
  onClose,
}) => {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute top-10 right-2 bg-white rounded-md shadow-lg py-1 z-20 w-40 border border-gray-200"
    >
      <button
        onClick={() => {
          onEdit(campaign._id);
          onClose();
        }}
        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
      >
        <FaRegEdit className="mr-2 text-[1rem] mb-1 text-blue-600" />
        Edit
      </button>
      {!campaign?.isSpecial && (
        <button
          onClick={() => {
            onMakeSpecial(campaign._id);
            onClose();
          }}
          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
        >
          <FaCrown className="mr-2 text-[1rem] mb-1 text-yellow-500" />
          Make Special
        </button>
      )}

      <button
        onClick={() => {
          onDelete(campaign._id);
          onClose();
        }}
        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
      >
        <MdDeleteOutline className="mr-2 text-[1.1rem] " />
        Delete
      </button>
    </div>
  );
};

const SpecialCampaign = () => {
  const ITEMS_PER_PAGE = 6;
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingCampaignId, setDeletingCampaignId] = useState("");
  const [openDropdown, setOpenDropdown] = useState(null); // Track which dropdown is open
  const navigate = useNavigate();
  //GET ALL CAMPAIGNS
  const getCampaignData = async () => {
    try {
      const response = await axiosInstance.get("/campaigns/all-campaigns");
      const filtered = response.data?.filter((cmp) => cmp?.isSignificant);

      setAllCampaigns(filtered);
      setFilteredCampaigns(filtered);
      setError(false);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  //SEARCH HANDLER
  const onSearchHandler = (query) => {
    setSearchTerm(query);
    const lowerCaseQuery = query.trim().toLowerCase();
    const results = allCampaigns.filter((campaign) =>
      campaign.name.toLowerCase().includes(lowerCaseQuery),
    );
    setFilteredCampaigns(results);
    setCurrentPage(1); // Reset to first page after search
  };
  const truncateText = (text, maxLength = 20) => {
    if (!text) return "";
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  };

  const onAddNewHandler = () => {
    setEditData(null);
    setModalOpen(true);
  };

  const handleOpenDeleteModal = (id) => {
    setDeletingCampaignId(id);
    setIsDeleteModalOpen(true);
    setOpenDropdown(null); // Close dropdown when opening delete modal
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setDeletingCampaignId(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingCampaignId) return;
    try {
      setIsDeleting(true);
      await axiosInstance.delete(
        `/campaigns/delete-campaign/${deletingCampaignId}`,
      );
      toast.success("Campaign deleted successfully", {
        id: deletingCampaignId,
      });
      getCampaignData();
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete. Try again.");
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setDeletingCampaignId(null);
    }
  };

  const handleEdit = (id) => {
    const campaign = allCampaigns.find((c) => c._id === id);
    setEditData(campaign);
    setModalOpen(true);
    setOpenDropdown(null); // Close dropdown when opening edit modal
  };

  const handleMakeSpecial = async (id) => {
    const campaign = allCampaigns.find((c) => c._id === id);

    try {
      const resp = await axiosInstance.post(
        `/campaigns/special-camp/${campaign._id}`,
      );

      if (resp) {
        toast.success(
          `"${truncateText(campaign.name)}" marked as special campaign!`,
        );
      }
      getCampaignData();
    } catch (error) {
      console.error("Error making the campaign special", error);
      toast.error("Failed to mark as special");
    }
  };

  const handleModalSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      // console.log("Form data:", formData);
      if (editData) {
        await axiosInstance.put(
          `/campaigns/edit-campaign/${editData._id}`,
          formData,
        );
        toast.success("Campaign updated");
      } else {
        // console.log("OnEdit mode :", formData);
        await axiosInstance.post("/campaigns/create-campaign", formData);
        toast.success("Campaign created");
      }
      getCampaignData();
      setModalOpen(false);
    } catch (err) {
      console.error("Error:", err);
      toast.error("Operation failed", { id: 1 });
    } finally {
      setIsSubmitting(false);
      setEditData(null);
    }
  };

  const toggleDropdown = (campaignId) => {
    setOpenDropdown(openDropdown === campaignId ? null : campaignId);
  };

  useEffect(() => {
    getCampaignData();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const totalPages = Math.ceil(filteredCampaigns.length / ITEMS_PER_PAGE);
  const paginatedCampaigns = filteredCampaigns.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  return (
    <div>
      <AdminSearchBar
        title={"Special Campaigns"}
        onAddNewClick={onAddNewHandler}
        onSearchClick={onSearchHandler}
      />
      <div className="h-auto overy">
        <div className="w-full py-4 mt-8 md:py-10 px-4 md:px-8 xl:px-[5rem]">
          {loading ? (
            <CampaignSkeletonLoader />
          ) : error ? (
            <div className="text-center text-red-500 font-semibold text-lg py-12">
              Failed to load campaigns. Please try again later.
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <NoCampaignFound query={searchTerm} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {paginatedCampaigns.map((campaign) => (
                <div
                  key={campaign._id}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 relative"
                >
                  {/* Special Campaign Badge */}
                  {campaign.isSpecial && (
                    <div className="absolute top-2 left-2 cursor-default bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded-md z-10 flex items-center">
                      <FaCrown className="mr-1" /> Special
                    </div>
                  )}

                  <div
                    onClick={() => {
                      navigate(`/special-campaign-details`, {
                        state: { campaign },
                      });
                    }}
                    className="relative h-56 overflow-hidden group"
                  >
                    <img
                      src={campaign.images[0]}
                      alt={campaign.title}
                      className="w-full h-full object-cover cursor-pointer transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-20 transition-all duration-300" />
                  </div>

                  {/* DESCRIPTION CONTAINER */}
                  <div className="flex flex-col gap-y-2 px-4 pt-2 pb-4">
                    <div
                      onClick={() => {
                        navigate(`/admin-campaign-details`, {
                          state: { campaign },
                        });
                      }}
                      className="text-md cursor-pointer font-semibold text-gray-800 min-h-12 line-clamp-2 text-ellipsis hover:text-blue-600 transition-colors"
                    >
                      {campaign.name}
                    </div>

                    {/* <Range
                      percentage={getPercentage(campaign.raised, campaign.goal)}
                    />

                    <div className="raised_and_goal pt-2 flex items-center justify-between text-xs md:text-sm text-[#5C5C5C]">
                      <div className="raised">
                        Raised:{" "}
                        <span className="font-semibold">
                          ₹{campaign.raised}
                        </span>
                      </div>
                      <div className="goal">
                        Goal:{" "}
                        <span className="font-semibold">
                          ₹{campaign.goal || 90000}
                        </span>
                      </div>
                    </div> */}
                  </div>

                  {/* DROPDOWN MENU BUTTON */}
                  <div className="absolute top-2 right-2 z-10">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        toggleDropdown(campaign._id);
                      }}
                      className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-all duration-200"
                      aria-label="Campaign options"
                    >
                      <FaEllipsisV className="text-gray-600" />
                    </button>

                    <CampaignDropdownMenu
                      campaign={campaign}
                      onEdit={handleEdit}
                      onDelete={handleOpenDeleteModal}
                      onMakeSpecial={handleMakeSpecial}
                      isOpen={openDropdown === campaign._id}
                      onClose={() => setOpenDropdown(null)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* PAGINATION */}
      <Pagination
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        totalPages={totalPages}
      />

      <SpecialCampaignModal
        isOpen={modalOpen}
        isSubmitting={isSubmitting}
        onClose={() => {
          setModalOpen(false);
          setEditData(null);
        }}
        onSubmit={handleModalSubmit}
        initialData={editData}
        isEditMode={!!editData}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        onClose={handleCancelDelete}
        loading={isDeleting}
        itemName={"this Campaign "}
      />
    </div>
  );
};

export default SpecialCampaign;
